import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useSettings } from "../context/SettingsContext";
import { usePremium } from "../context/PremiumContext";
import { getExplain } from "../api";

const DARK_SCRIM = "rgba(8,10,14,0.66)";
const LIT_SCRIM = "rgba(8,10,14,0)";

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
const q = (id) => document.querySelector(`[data-testid="${id}"]`);

// Click by testid, falling back to matching visible button text (spec C).
function clickId(id, texts = []) {
  const el = q(id);
  if (el) { el.click(); return true; }
  if (texts.length) {
    const els = Array.from(document.querySelectorAll('button,[role="button"],a'));
    const hit = els.find((e) => {
      const t = (e.textContent || "").trim().toLowerCase();
      return texts.some((x) => t.includes(x));
    });
    if (hit) { hit.click(); return true; }
  }
  return false;
}

function findEl({ testids = [], texts = [] }) {
  for (const id of testids) { const el = q(id); if (el) return el; }
  if (texts.length) {
    const els = Array.from(document.querySelectorAll('button,[role="button"],a,h1,h2'));
    return els.find((e) => {
      const t = (e.textContent || "").trim().toLowerCase();
      return texts.some((x) => t.includes(x));
    }) || null;
  }
  return null;
}

async function waitForEl(spec, timeout = 5000) {
  const start = Date.now();
  while (Date.now() - start < timeout) {
    const el = findEl(spec);
    if (el) return el;
    await sleep(150);
  }
  return null;
}

// easeOutQuad scroll, distance-scaled duration, recomputes end each frame (spec E).
function smoothScrollTo(getTargetY, reduced) {
  return new Promise((resolve) => {
    const startY = window.scrollY;
    if (Math.abs(getTargetY() - startY) < 40) return resolve();
    if (reduced) { window.scrollTo(0, getTargetY()); return resolve(); }
    const dur = Math.min(1800, Math.max(650, Math.abs(getTargetY() - startY) / 1.1));
    const t0 = performance.now();
    const tick = (now) => {
      const p = Math.min(1, (now - t0) / dur);
      const eased = 1 - (1 - p) * (1 - p);
      window.scrollTo(0, startY + (getTargetY() - startY) * eased);
      if (p < 1) requestAnimationFrame(tick);
      else resolve();
    };
    requestAnimationFrame(tick);
  });
}

const CH = "/read/PSA/23";

const STEPS = [
  { id: "translation", route: "/", target: { testids: ["landing-translation"], texts: ["translation"] },
    title: "Choose your translation", body: "Pick the version you love — KJV, WEB, ASV, NLT and more." },
  { id: "browse", route: "/", target: { testids: ["browse-books"], texts: ["browse", "books"] },
    title: "Open the Word", body: "Jump to any book and chapter. Let's open Psalm 23." },
  { id: "read", route: CH, lit: true, scrollEndOnNext: true,
    title: "Read, beautifully", body: "A calm, distraction-free reader — and as you scroll, the chapter artwork quietly comes to life. Tap Next to read to the end." },
  { id: "explain-btn", route: CH, lit: true, target: { testids: ["open-explain"], texts: ["explain"] },
    title: "Explain — right in your toolbar", body: "See 'Explain' in the toolbar below? Tap it on any chapter, any time." },
  { id: "explain-open", route: CH, lit: true, target: { testids: ["explain-panel"] },
    then: async () => {
      clickId("open-explain", ["explain"]);
      await sleep(350);
      clickId("explain-choice-chapter", ["whole chapter", "whole"]);
      await sleep(500);
    },
    title: "Understand any verse", body: "We opened Explain for you — the whole chapter in plain language." },
  { id: "levels", route: CH, lit: true, target: { testids: ["explain-levels"] }, closeOnNext: true,
    title: "Four reading levels", body: "Read it as a child, teen, adult, or scholar — whatever fits the moment." },
  { id: "recap", route: CH, lit: true, target: { testids: ["chapter-recap"] },
    title: "The chapter in one sentence", body: "At the end of every chapter, a one-sentence summary helps it stick." },
  { id: "memory", route: CH, open: "memory-check-start", appear: "memory-check-active", target: { testids: ["memory-check-active"] },
    title: "A quick memory check", body: "Answer one short question to lock in what you just read." },
  { id: "artwork", route: CH, lit: true, open: "view-artwork-btn", appear: "artwork-overlay", target: { testids: ["artwork-overlay"] },
    title: "Reveal your reward", body: "Reach the end of a chapter and unlock its full, beautiful artwork." },
  { id: "tree", route: "/", target: { testids: ["focus-journey-scene"] },
    title: "Your Focus Tree", body: "Every chapter you read grows a living tree — a gentle picture of your journey." },
  { id: "goal", route: "/", open: "set-goal-btn", appear: "goal-dialog", target: { testids: ["goal-dialog"] }, closeOnNext: true,
    title: "Set your bloom goal", body: "Choose how many chapters you'd like to read and your tree grows with you." },
  { id: "theme", route: "/", tapOnce: "theme-toggle-header", target: { testids: ["theme-toggle-header"] }, endDark: true, lit: true,
    title: "Day or night", body: "Watch — we just switched to light mode. Read whichever way feels calm." },
  { id: "menu", route: "/", open: "open-menu", appear: "app-menu", target: { testids: ["app-menu"] }, last: true,
    title: "All your Bible tools", body: "Devotionals, plans, prayer journal, memory and more all live right here." },
];

export default function GuidedTour() {
  const navigate = useNavigate();
  const location = useLocation();
  const { setFocusMode, setTheme } = useSettings();
  const { inApp } = usePremium();

  const [running, setRunning] = useState(false);
  const [step, setStep] = useState(0);
  const [rect, setRect] = useState(null);
  const [entering, setEntering] = useState(false);
  const [busy, setBusy] = useState(false);
  const rafRef = useRef(0);
  const reduced = typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

  const shouldRun = useCallback(() => {
    try {
      // ?tour=1 lets anyone preview the full guided tour on the web.
      if (new URLSearchParams(window.location.search).get("tour") === "1") {
        localStorage.setItem("cb_force_tour", "1");
        localStorage.removeItem("cb_tour_done");
        return true;
      }
      if (localStorage.getItem("cb_force_tour") === "1" || localStorage.getItem("force_tour") === "1") return true;
      if (localStorage.getItem("cb_tour_done") === "1") return false;
      return inApp && localStorage.getItem("fw_onboarded") !== "1" && localStorage.getItem("cb_tour_done") !== "1";
    } catch { return false; }
  }, [inApp]);

  useEffect(() => {
    if (running) return;
    if (!shouldRun()) return;
    let cancelled = false;
    (async () => {
      try { localStorage.setItem("cb_tour_active", "1"); } catch { /* ignore */ }
      setFocusMode(true);
      getExplain("PSA", 23).catch(() => {}); // prewarm Explain for the demo
      navigate(CH);
      await sleep(1400);
      if (!cancelled) { setStep(0); setRunning(true); }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldRun]);

  const cur = running ? STEPS[step] : null;

  useEffect(() => {
    if (!running || !cur) return;
    let active = true;
    setEntering(true);
    setRect(null);
    (async () => {
      if (cur.route && location.pathname !== cur.route) { navigate(cur.route); await sleep(1300); }
      else await sleep(150);
      if (!active) return;
      if (cur.tapOnce) { clickId(cur.tapOnce); await sleep(500); }
      if (cur.open) {
        for (let i = 0; i < 6 && active; i++) {
          if (cur.appear && q(cur.appear)) break;
          clickId(cur.open);
          await sleep(500);
        }
      }
      if (cur.then) { try { await cur.then(); } catch { /* ignore */ } }
      if (!active) return;
      if (!cur.target) { setEntering(false); return; }
      const el = await waitForEl(cur.target);
      if (!active) return;
      if (el) { try { el.scrollIntoView({ block: "center", behavior: "smooth" }); } catch { /* ignore */ } await sleep(400); }
      if (!active) return;
      setEntering(false);
      const track = () => {
        const n = findEl(cur.target);
        if (n) { const r = n.getBoundingClientRect(); setRect({ top: r.top, left: r.left, width: r.width, height: r.height }); }
        rafRef.current = requestAnimationFrame(track);
      };
      track();
    })();
    return () => { active = false; cancelAnimationFrame(rafRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [running, step]);

  const finish = useCallback(() => {
    cancelAnimationFrame(rafRef.current);
    setTheme("dark");
    try {
      localStorage.setItem("cb_tour_active", "0");
      localStorage.setItem("cb_tour_done", "1");
      localStorage.setItem("fw_onboarded", "1");
      localStorage.removeItem("cb_force_tour");
      localStorage.removeItem("force_tour");
    } catch { /* ignore */ }
    document.dispatchEvent(new KeyboardEvent("keydown", { key: "Escape", bubbles: true }));
    setRunning(false);
    window.dispatchEvent(new Event("cb-tour-finished"));
  }, [setTheme]);

  const advance = async () => {
    if (busy || !cur) return;
    setBusy(true);
    cancelAnimationFrame(rafRef.current);
    if (cur.scrollEndOnNext) await smoothScrollTo(() => document.body.scrollHeight, reduced);
    if (cur.closeOnNext) { document.dispatchEvent(new KeyboardEvent("keydown", { key: "Escape", bubbles: true })); await sleep(350); }
    if (cur.endDark) { setTheme("dark"); await sleep(300); }
    setBusy(false);
    if (cur.last) { finish(); return; }
    setStep((s) => s + 1);
  };

  if (!running || !cur) return null;

  const scrim = cur.lit ? LIT_SCRIM : DARK_SCRIM;
  const pad = 8;
  const hasRect = !!rect;

  let cardStyle = { left: "50%", top: "50%", transform: "translate(-50%,-50%)" };
  if (hasRect) {
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const tall = rect.height > vh * 0.55;
    if (tall) {
      const lx = rect.left > 340 ? rect.left / 2 : Math.max(170, rect.left - 170);
      cardStyle = { left: Math.min(Math.max(lx, 170), vw - 170), top: "50%", transform: "translate(-50%,-50%)" };
    } else {
      const below = rect.top + rect.height + 270 < vh;
      const rawTop = below ? rect.top + rect.height + 16 : rect.top - 262;
      const top = Math.min(Math.max(rawTop, 90), vh - 250);
      cardStyle = { left: Math.min(Math.max(rect.left + rect.width / 2, 170), vw - 170), top, transform: "translateX(-50%)" };
    }
  }

  return (
    <div className="fixed inset-0 z-[250]" data-testid="guided-tour" style={{ pointerEvents: "none" }}>
      <style>{`
        @keyframes cbTourPulse {
          0% { box-shadow: 0 0 0 0 rgba(96,143,255,0.55); }
          70% { box-shadow: 0 0 0 14px rgba(96,143,255,0); }
          100% { box-shadow: 0 0 0 0 rgba(96,143,255,0); }
        }
        .cb-next-pulse { animation: cbTourPulse 1.8s infinite; }
        @media (prefers-reduced-motion: reduce) { .cb-next-pulse { animation: none; } }
      `}</style>

      {hasRect ? (
        <div
          style={{
            position: "fixed",
            top: rect.top - pad,
            left: rect.left - pad,
            width: rect.width + pad * 2,
            height: rect.height + pad * 2,
            borderRadius: 16,
            boxShadow: `0 0 0 9999px ${scrim}`,
            transition: "all 0.25s ease",
            pointerEvents: "none",
          }}
        />
      ) : (
        <div style={{ position: "fixed", inset: 0, background: scrim }} />
      )}

      {!entering && (
        <div
          className="font-ui absolute w-[300px] max-w-[86vw] rounded-3xl border border-border bg-background p-5 shadow-2xl"
          style={{ ...cardStyle, pointerEvents: "auto" }}
          data-testid="tour-card"
        >
          <p className="font-ui mb-1 text-xs font-semibold uppercase tracking-[0.18em] text-primary/80">
            Step {step + 1} of {STEPS.length}
          </p>
          <h3 className="font-serif-display text-xl tracking-tight">{cur.title}</h3>
          <p className="font-ui mt-2 text-sm text-muted-foreground">{cur.body}</p>
          <div className="mt-5 flex items-center justify-between gap-3">
            <button onClick={finish} data-testid="tour-skip" className="text-sm text-muted-foreground hover:text-foreground">
              Skip
            </button>
            <button
              onClick={advance}
              disabled={busy}
              data-testid="tour-next"
              className="cb-next-pulse rounded-full bg-primary px-6 py-3 text-sm font-bold text-primary-foreground transition-transform hover:scale-105 disabled:opacity-60"
            >
              {cur.last ? "Finish" : "Next"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
